import {ScreenLoader} from "@/utils/loading.util";
import moment from "moment";
import {TimeSlotUtil} from "@/utils/timeslot.util";
import router from "@/router";
import {Constants} from "@/constants";
import {EntityBooking, EntityUtilityService} from "@/services/entity.service";
import Loader from '../../components/loader/Loader'
import {CookieService} from "@/services/cookie.service";
import {ref, toRefs} from "vue";
import {Account, AccountService} from "@/services/account.service";

export default {
    name: 'VisitsTimeSlotContainer',

    components: {
        Loader
    },

    props: {
        arrivalDate: Date,
        activeTimeSlots: Array,
        screenLoader: ScreenLoader,
        filteredSpots: Array,
        filteredEntities: Array,
        entityMode: false,
        timeSlots: Array,
        entities: Array,
        location: Object,
        spots: Array,
        spotBookings: Array,
        showCard: false,
        selectedOption: Object
    },

    data() {
        return {
            pageSize: 15,
            accountCustomers: undefined,
            account: undefined
        }
    },
    async mounted () {
        if (process.env.VUE_APP_PAGE_SIZE) this.pageSize = Number.parseInt(process.env.VUE_APP_PAGE_SIZE)
        this.pageNumber = this.getPageNumber(this.location)
        await this.getAccount()
    },

    setup (props) {
        let pageNumber = ref(1)
        const cookie = CookieService.getCookie("page")
        if (cookie) {
            const data = new Map(Object.entries(cookie))
            const {location} = toRefs(props)
            const page = data.get(location.value.id)
            if (page) {
                pageNumber = page
            }
        }
        return {
            pageNumber
        }
    },

    watch: {
      location(location) {
        this.pageNumber = this.getPageNumber(location)
      }
    },

    // return different status for bookings
    computed: {

       statusRequested() {
            return Constants.STATUS_REQUESTED
        },

        statusAccepted() {
            return Constants.STATUS_ACCEPTED
        },

        statusRejected() {
            return Constants.STATUS_REJECTED
        },

        sortedEntities() {
            const sortedEntities = this.filteredEntities.sort((a, b) => {
                if ( a.name < b.name ){
                    return -1;
                }
                if ( a.name > b.name ){
                    return 1;
                }
                return 0;
            })

            return sortedEntities
        },

        numPages () {
            return Math.ceil(this.sortedEntities.length / this.pageSize); // Math.ceil ensures rounding up
        },

        pagedEntities () {
            const start = (this.pageSize * this.pageNumber) - this.pageSize
            const end = start + this.pageSize
            return this.sortedEntities.slice(start, end)
        }
    },

    methods: {

        pixelLength(entity) {
            return  `${(entity.timeSlotLengthInMinutes / this.location.timeSlotLengthInMinutes) * 50}px`
        },

        // set the account customers
        getAccount: async function() {
            let account = await AccountService.getLoggedInAccount().catch((error) => {
                this.screenLoader.stopWithError(error, ['filter'])
            })
            this.account = account
            this.accountCustomers = account.customers ?? []
        },

        findIndex(entity) {
            return this.entities.indexOf(this.entities.find(e => e.id === entity.id))
        },
        /**
         * Checks whether the timeslot is at the current moment in time
         * @param entityIndex the index of the entity in this.activeTimeSlots
         * @param timeSlotIndex the index of the timeslot in this.activeTimeSlots
         * @returns {boolean} whether or not the timeslot is at the current moment in time
         */
        isTimeSlotCurrent(entityIndex, timeSlotIndex) {
            let timeSlot
            if (entityIndex) {
                timeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]
            } else {
                timeSlot = this.timeSlots[timeSlotIndex]
            }
            let locationOffset = moment().tz(this.location.timezoneName).utcOffset()
            let correctedMoment = moment().add(locationOffset - moment().utcOffset(), 'm')
            const currentTime = TimeSlotUtil.createTimeSlotByDate(correctedMoment)

            let minutes = currentTime.minutes - (currentTime.minutes % (entityIndex ? this.entities[entityIndex].timeSlotLengthInMinutes : this.location.timeSlotLengthInMinutes))
            const isCurrentDay = moment().startOf('day').isSame(moment(this.arrivalDate).startOf('day'))

            return timeSlot.hours === currentTime.hours && timeSlot.minutes === minutes && isCurrentDay
        },

        /**
         * The the booking for a timeslot
         * @param entityIndex the index of the entity in this.entities and this.activeTimeSlots
         * @param timeSlotIndex the index of the timeslot in this.activeTimeSlots
         * @param status the status of the booking
         * @returns {boolean|undefined}
         */
        getTimeSlotBooking(entityIndex, timeSlotIndex, status) {
            const bookings = this.entities[entityIndex].bookings
            const timeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]

            if (TimeSlotUtil.isTimeSlotBooked(bookings, timeSlot, this.arrivalDate, status, this.accountCustomers)) {
                const booking = TimeSlotUtil.getTimeSlotBooking(bookings, timeSlot, this.arrivalDate)
                return booking.startTimeSlot.index === timeSlotIndex ? booking : undefined
            }
        },

        getTimeSlotBookingStatusUnknown(entityIndex, timeSlotIndex) {
            const bookings = this.entities[entityIndex].bookings
            const timeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]
            const statuses = [this.statusAccepted, this.statusRequested, this.statusRejected]

            let result = undefined
            statuses.forEach(status => {
                if (TimeSlotUtil.isTimeSlotBooked(bookings, timeSlot, this.arrivalDate, status, this.accountCustomers)) {
                    const booking = TimeSlotUtil.getTimeSlotBooking(bookings, timeSlot, this.arrivalDate)
                    if(booking?.startTimeSlot?.index === timeSlotIndex) {
                        result = booking
                    }
                }
            })
            return result
        },

        /**
         * Check whether a timeslot of a spot is booked
         * @param spot the spot in this.spots
         * @param timeslot the timeslot
         * @param timeslotIndex the index of the timeslot
         * @param status the status of the booking
         * @returns {boolean|undefined}
         */
        isTimeSlotSpotBooked(spot, timeslot, timeslotIndex, status) {
            if (TimeSlotUtil.isTimeSlotBooked(this.spotBookings[this.spots.indexOf(spot)], timeslot, this.arrivalDate, status, this.accountCustomers)) {
                const booking = TimeSlotUtil.getTimeSlotBooking(this.spotBookings[this.spots.indexOf(spot)], timeslot, this.arrivalDate)
                return booking.startTimeSlot.index === timeslotIndex ? booking : undefined
            }
        },

        /**
         * Triggered when clicking on a timeslot, either opens the card if the timeslot is booked or opens the booking page
         * @param entityIndex the index of the entity in this.entities and this.activeTimeSlots
         * @param timeSlotIndex  the index of the timeslot in this.activeTimeSlots
         */
        selectTimeSlot(entityIndex, timeSlotIndex) {
            const entity = this.entities[entityIndex]
            const startTimeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]
            const booking = TimeSlotUtil.getTimeSlotBooking(entity.bookings, startTimeSlot, this.arrivalDate)
            const timeslotStatus = this.isTimeSlotBooked(entityIndex, timeSlotIndex)
            if (!timeslotStatus.timeslotBlocked) {
                if (booking && booking.customer !== undefined && timeslotStatus.timeslotBooked) {
                    this.selectedBooking = booking
                    this.$parent.toggleCard()
                    return
                }
            }

            if (!timeslotStatus.timeslotBlocked &&!timeslotStatus.timeslotBooked) {
                const start = moment(this.arrivalDate).set({ hours: startTimeSlot.hours, minutes: startTimeSlot.minutes, seconds: 0, milliseconds: 0 }).toISOString()
                const end = moment(start).add(entity.timeSlotLengthInMinutes, 'minutes').toISOString()
                router.push(`/booking/?locationId=${this.location.id}&entityId=${entity.id}&startDate=${start}&endDate=${end}`)
            }
        },

        /**
         * Check if timeslot is blocked
         * @param entityIndex the index of the entity in this.entities and this.activeTimeSlots
         * @param timeSlotIndex  the index of the timeslot in this.activeTimeSlots
         * @returns {boolean}
         */
        isTimeSlotBlocked(entityIndex, timeSlotIndex) {
            const isTimeSlotBooked = this.isTimeSlotBooked(entityIndex, timeSlotIndex)
            const isTimeSlotRequested = this.isTimeSlotBooked(entityIndex, timeSlotIndex, Constants.STATUS_REQUESTED)
            const isTimeSlotAccepted = this.isTimeSlotBooked(entityIndex, timeSlotIndex, Constants.STATUS_ACCEPTED)
            return isTimeSlotBooked && !isTimeSlotRequested && !isTimeSlotAccepted
        },

        /**
         * Checks whether a timeslot for a given entity conforms to status.
         * @param entityIndex Index of entity in this.entities
         * @param timeSlotIndex Index of timeslot in this.timeslots
         * @param status The status for which is checked. If undefined, then it is checked if it is booked
         * @returns {{timeslotBlocked: (*|boolean), timeslotBooked: (*|boolean)}}
         **/
        isTimeSlotBooked(entityIndex, timeSlotIndex, status) {
            const entity = this.entities[entityIndex]
            let entityStartTime = TimeSlotUtil.getTimeSlotTime(entity.timeSlotStartTime)
            let entityEndTime = TimeSlotUtil.getTimeSlotTime(entity.timeSlotEndTime)

            entityStartTime = moment(this.arrivalDate).set({ hours: entityStartTime.hours(), minutes: entityStartTime.minutes(), seconds: 0, milliseconds: 0 })
            entityEndTime = moment(this.arrivalDate).set({ hours: entityEndTime.hours(), minutes: entityEndTime.minutes(), seconds: 0, milliseconds: 0 })

            const timeSlot = this.activeTimeSlots[entityIndex][timeSlotIndex]
            const timeSlotStartTime = moment(this.arrivalDate).set({ hours: timeSlot.hours, minutes: timeSlot.minutes, seconds: 0, milliseconds: 0 })
            const timeSlotEndTime = moment(timeSlotStartTime).add(entity.timeSlotLengthInMinutes, 'minutes')

            let locationOffset = moment().tz(this.location.timezoneName).utcOffset()
            let correctedMoment = moment().add(locationOffset - moment().utcOffset(), 'm')
            const isFutureTimeSlot = correctedMoment.isBefore(timeSlotStartTime) || correctedMoment.isBetween(timeSlotStartTime, timeSlotEndTime)

            const isBetweenOpeningHours = timeSlot.isClosed

            const isAbleToBookInBookingWindow = EntityUtilityService.validateBookingWindow(timeSlotStartTime, correctedMoment, entity)

            const isPrivate = TimeSlotUtil.isTimeslotPrivate(entity.bookings, timeSlot, this.arrivalDate, this.location.bookingsPrivateForAgent, this.account)


            const isTimeSlotBooked = TimeSlotUtil.isTimeSlotBooked(entity.bookings, timeSlot, this.arrivalDate, undefined, this.accountCustomers)
            const isTimeSlotRequested = TimeSlotUtil.isTimeSlotBooked(entity.bookings, timeSlot, this.arrivalDate, Constants.STATUS_REQUESTED, this.accountCustomers)
            const isTimeSlotAccepted = TimeSlotUtil.isTimeSlotBooked(entity.bookings, timeSlot, this.arrivalDate, Constants.STATUS_ACCEPTED, this.accountCustomers)
            const timeslotBlocked =  isTimeSlotBooked && !isTimeSlotRequested && !isTimeSlotAccepted
            return  {timeslotBooked: isTimeSlotBooked , timeslotBlocked: timeslotBlocked || !isAbleToBookInBookingWindow || !isFutureTimeSlot || !isBetweenOpeningHours, private: isPrivate}
        },

        /**
         * Checks whether the current time is not before a timeslot or in a timeslot
         * @param timeslot
         * @returns {boolean} whether or not a timeslot starts and ends after the current time
         */
        isFutureTimeslot (timeslot) {
            const timeSlotStartTime = moment(this.arrivalDate).set({ hours: timeslot.hours, minutes: timeslot.minutes, seconds: 0, milliseconds: 0 })
            const timeSlotEndTime = moment(timeSlotStartTime).add(15, 'minutes')
            let locationOffset = moment().tz(this.location.timezoneName).utcOffset()
            let correctedMoment = moment().add(locationOffset - moment().utcOffset(), 'm')
            return correctedMoment.isBefore(timeSlotStartTime) || correctedMoment.isBetween(timeSlotStartTime, timeSlotEndTime)
        },

        /**
         * Triggered when clicking on a timeslot in spot mode, either opens the card if the timeslot is booked or opens the booking page
         * @param spot the selected spot
         * @param timeslot the selected timeslot
         * @param timeslotIndex the index of the selected timeslot in this.timeSlots
         */
        selectTimeslotInOverview (spot, timeslot, timeslotIndex) {
            const booking = TimeSlotUtil.getTimeSlotBooking(this.spotBookings[this.spots.indexOf(spot)], timeslot, this.arrivalDate)

            // if there is no booking, create one:
            if (booking === undefined) {
                const startTimeSlot = this.timeSlots[timeslotIndex]
                const start = moment(this.arrivalDate).set({
                    hours: startTimeSlot.hours,
                    minutes: startTimeSlot.minutes,
                    seconds: 0,
                    milliseconds: 0
                }).toISOString()
                const end = moment(start).add(15, 'minutes').toISOString()
                router.push(`/booking/?locationId=${this.location.id}&startDate=${start}&endDate=${end}&spotId=${spot.id}&label=${spot.label}`)
            } else {
                if (booking && (booking.customer !== undefined)) {
                    this.$parent.selectedBooking = new EntityBooking(booking)
                    this.$parent.toggleCard()
                }
            }
        },

        // v-if on this to only show entites that are included in the selected groups (in entity mode)
        isEntityChecked(entity) {
            return this.filteredEntities.map(e => e.id).includes(entity.id) //
        },

        // shows info for a booking in spot mode if there is any
        getSpotBookingInfo(booking) {
            const options = booking.options ? booking.options.filter(option => option.usedForVisitsDisplay) : []
            return options.length === 0 ? "" : options[0].value
        },

        getBookingClass(entityIndex, timeSlotIndex) {

            if (this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusRequested) && this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusRequested) !== undefined ) {
                return "timeslot-visits-booked timeslot-requested"
            } else if (this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusAccepted) && this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusAccepted) !== undefined) {
                return "timeslot-visits-booked timeslot-accepted"
            } else if (this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusRejected) && this.getTimeSlotBooking(entityIndex, timeSlotIndex, this.statusRejected) !== undefined) {
                return "timeslot-visits-booked timeslot-rejected"
            }

            return undefined
        },

        changePage(number) {
            this.pageNumber += number
            const cookie = CookieService.getCookie("page")
            const data = cookie ?  new Map(Object.entries(cookie)) : new Map()
            data.set(this.location.id, this.pageNumber)
            CookieService.storeCookie("page", Object.fromEntries(data), 365)
        },

        getPageNumber(location) {
            const cookie = CookieService.getCookie("page")
            if (cookie) {
                const data = new Map(Object.entries(cookie))
                const page = data.get(location.id)
                if (page) return page
            }
            return 1
        }
    }
}
